import { translate } from 'sharedreact/utils/LanguageSupport';
import NumberSupport from 'sharedreact/utils/NumberSupport';
import Encode from 'sharedreact/utils/Encode';

function strictEncodeURIComponent (str: string): string {
  return Encode.normalizeHexChars(encodeURIComponent(str).replace(/[!'()*]/g, (c) => '%' + c.charCodeAt(0).toString(16)));
}

const SIZE_MULTIPLIER: number = 1024;
const KB: number = SIZE_MULTIPLIER;
const MB: number = SIZE_MULTIPLIER * SIZE_MULTIPLIER;
const GB: number = SIZE_MULTIPLIER * SIZE_MULTIPLIER * SIZE_MULTIPLIER;
const TB: number = SIZE_MULTIPLIER * SIZE_MULTIPLIER * SIZE_MULTIPLIER * SIZE_MULTIPLIER;

function formatFileSize (size: number): string {
  if (isNaN(size)) {
    return '-';
  } else {
    if (size < KB) {
      return translate('shared.general.filesize.b', size);
    } else if (size < MB) {
      const fileSize: string = NumberSupport.numberToString(size / KB, 0);
      return translate('shared.general.filesize.kb', fileSize);
    } else if (size < GB) {
      const fileSize: string = NumberSupport.numberToString(size / MB, 1);
      return translate('shared.general.filesize.mb', fileSize);
    } else if (size < TB) {
      const fileSize: string = NumberSupport.numberToString(size / GB, 2);
      return translate('shared.general.filesize.gb', fileSize);
    } else {
      const fileSize: string = NumberSupport.numberToString(size / TB, 3);
      return translate('shared.general.filesize.tb', fileSize);
    }
  }
}

// deprecated, use normalizeUrlString instead
function ToUrlString (path: string): string {
  if (path.startsWith('\\\\')) {
    const url = 'file://' + path.replace(/^\\\\/, '').split(/[\\/]/).map(component => strictEncodeURIComponent(component)).join('/');
    return Encode.normalizeHexChars(url);
  } else if (path.startsWith('//')) {
    const url = 'file://' + path.replace(/^\/\//, '').split(/[\\/]/).map(component => strictEncodeURIComponent(component)).join('/');
    return Encode.normalizeHexChars(url);
  } else if (/^([a-z0-9]+:\/\/)/.test(path)) {
    if (path.includes('\\') || path.includes(' ')) {
      const matchResult = path.match(/^([a-z0-9]+:\/\/)/);
      if (matchResult != null) {
        const url = matchResult[1] + path.replace(/^([a-z0-9]+:\/\/)/, '').split(/[\\/]/).map(component => strictEncodeURIComponent(decodeURIComponent(component))).join('/');
        return Encode.normalizeHexChars(url);
      }
    }
  }

  return path;
}

function normalizeUrlString (path: string, dontErrorOnInvalidCharacters?: boolean): string {
  // this function will:
  // * fix prefix
  // * replace \ by /
  // * lowercase host name
  // * escape spaces
  // * escape characters !'()*
  // * lowercase hex characters

  // get correct prefix + split components
  let prefix: string = 'file://';
  let pathParts: string [] = [];
  if (path.startsWith('\\\\')) {
    pathParts = path.replace(/^\\\\/, '').split(/[\\/]/);
  } else if (path.startsWith('//')) {
    pathParts = path.replace(/^\/\//, '').split(/[\\/]/);
  } else {
    const matchResult = path.match(/^([a-z0-9]+:\/\/)/);
    if (matchResult != null) {
      prefix = matchResult[1];
      pathParts = path.replace(/^([a-z0-9]+:\/\/)/, '').split(/[\\/]/).map(component => {
        if (dontErrorOnInvalidCharacters) {
          try {
            return decodeURIComponent(component);
          } catch (error) {
          }
        } else {
          return decodeURIComponent(component);
        }
        return component;
      });
    } else {
      const matchResult = path.match(/^([a-z0-9]+%3[aA]%2[fF]%2[fF])/);
      if (matchResult != null) {
        prefix = decodeURIComponent(matchResult[1]);
        const decodedPath: string = decodeURIComponent(path);

        pathParts = decodedPath.replace(/^([a-z0-9]+:\/\/)/, '').split(/[\\/]/).map(component => {
          if (dontErrorOnInvalidCharacters) {
            try {
              return decodeURIComponent(component);
            } catch (error) {
            }
          } else {
            return decodeURIComponent(component);
          }
          return component;
        });
      } else {
        // unknown path type. so don't change
        return path;
      }
    }
  }

  if (pathParts.length > 0) {
    // lowercase host name
    if (prefix !== 'eskocloud://' && prefix !== 'ec://') {
      pathParts[0] = pathParts[0].toLowerCase();
    }
  }

  // escape special characters, lowercase hex characters for each component, then combine components using / + add prefix
  const url = prefix + pathParts.map(component => strictEncodeURIComponent(component)).join('/');
  return url;
}

function fileTypeName (fileType: string): string {
  const translatedFileType = translate('filetype.' + fileType);
  if (translatedFileType.startsWith('[') && translatedFileType.endsWith(']')) {
    return fileType;
  } else {
    return translatedFileType;
  }
}

function getFileIconForType (fileType: string): string {
  return 'esko_fileicon esko_fileicon-' + fileType.toLowerCase();
}

function getServerNameFromFilePath (url: string | null): string {
  if (url != null && url !== '/' && url !== '') {
    const match = url.match(/file:\/\/(\S*)\//);
    if (match != null) {
      const serverStr: string = match[1];
      const serverNameSeparatorindex: number = serverStr.indexOf('/');
      const serverName: string = serverStr.substring(0, serverNameSeparatorindex);
      return serverName;
    }
  }
  return '';
}

const FileSupport = {
  strictEncodeURIComponent,
  formatFileSize,
  ToUrlString, // deprecated, use normalizeUrlString instead
  normalizeUrlString,
  fileTypeName,
  getFileIconForType,
  getServerNameFromFilePath
};

export {
  formatFileSize
}

export default FileSupport;
